import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { FullPageLoader, Loader } from '@components';
import useChartById from '@hooks/api/useChartById';
import useCharts from '@hooks/api/useCharts';
import useCustomDashboardFilters from '@hooks/api/useCustomDashboardFilters';
import WeeklyChangelog from '../components/AdImpressions/WeeklyChangelog';
import AdImpressionsFilters from '../components/AdImpressions/AdImpressionsFilters';
import AdImpressionsIndexSection from '../components/AdImpressions/AdImpressionsIndexSection';
import { ChartData, ChartListData, Filter, WeekFilter } from '../components/AdImpressions/types';
import { AD_IMPRESSIONS_CHART_TYPE } from '../components/AdImpressions/constants';
import appMagicLogo from '../../../assets/icons/appmagic-logo.svg';

type GenericFilterEntry<T> = {
  name: string;
  displayName: string;
  field: string;
  metadata: string;
  values: T[];
};

const AdImpressions: React.FC<{ id: string }> = ({ id }) => {
  const [filters, setFilters] = useState<Filter[]>([]);

  const [selections, setSelections] = useState<Record<string, string>>({
    country: '',
    category_genre: '',
    start_date: '',
  });
  const [dashboardData, setDashboardData] = useState<ChartListData[]>([]);
  const [dashboardLoader, setDashboardLoader] = useState<boolean>(true);
  const [weekFilter, setWeekFilter] = useState<WeekFilter[]>([]);
  const [categoryCountryMap, setCategoryCountryMap] = useState<GenericFilterEntry<{
    name: string;
    countries: { country: string; country_code: string }[];
  }> | null>(null);

  const { getChartData } = useCharts();
  const { getChartDataById } = useChartById();
  const { getDashboardFilterValues } = useCustomDashboardFilters();

  const fetchDashboardData = async (
    dashboardData: ChartListData[],
    setChartData: React.Dispatch<ChartData[]>,
    setComponentLoader: React.Dispatch<React.SetStateAction<boolean>>,
    filters?: Record<string, string>,
  ) => {
    if (id && dashboardData?.length) {
      const chartsById: ChartData[] = [];
      setComponentLoader(true);
      try {
        await Promise.all(
          dashboardData.map(async (chart: ChartListData) => {
            const parsedMetadata = chart.metadata ? JSON.parse(chart.metadata) : {};
            const response = await getChartDataById(
              id,
              chart.id,
              filters,
              parsedMetadata?.sort?.fields,
              parsedMetadata?.sort?.order,
            );

            if (response) {
              chartsById.push({
                ...response,
                chartType: chart.chartType,
              });
            }
          }),
        );
        setChartData(chartsById);
      } catch (error) {
        console.error('Error fetching chart data:', error);
      } finally {
        setComponentLoader(false);
      }
    }
  };

  const mapGenres = (genres: any[]) =>
    genres.map((genre: { name: string }) => ({
      id: genre.name,
      label: genre.name.replace(/_/g, '-'),
    }));

  const mapCountries = (countries: any[]) =>
    countries.map((country: { country_code: string; country: string }) => ({
      id: country.country_code,
      label: country.country,
    }));

  const normalizeWeekValues = (values: any[]) =>
    values
      .map((v) => {
        if (typeof v === 'string') {
          return { week_start: v, week_end: v, week_number: v };
        } else if (typeof v === 'object') {
          return {
            week_start: v.week_start,
            week_end: v.week_end,
            week_number: v.week_number,
          };
        }
        return null;
      })
      .filter(
        (
          v,
        ): v is {
          week_start: string;
          week_end: string;
          week_number: string;
        } => v !== null,
      );

  const initializeChartData = async () => {
    setDashboardLoader(true);
    if (!id) return;

    try {
      const [chartResponse, filterResponse] = await Promise.all([
        getChartData(id),
        getDashboardFilterValues(id),
      ]);

      if (chartResponse) {
        setDashboardData(chartResponse);
      }

      if (filterResponse) {
        const categoryCountryMap = filterResponse['category_genre'];
        setCategoryCountryMap(categoryCountryMap);

        const categoryGenreOptions = mapGenres(categoryCountryMap?.values || []).sort((a, b) =>
          a.label.localeCompare(b.label),
        );

        const countryOptions = mapCountries(categoryCountryMap?.values?.[0]?.countries || []);

        const weekRawValues = filterResponse['start_date']?.values || [];
        const weekFilter = normalizeWeekValues(weekRawValues);

        setWeekFilter(
          weekFilter.map((week) => ({
            week_start: week.week_start,
            week_end: week.week_end,
            week_number: Number(week.week_number),
          })),
        );

        setFilters([
          {
            key: 'category_genre',
            label: 'Genre',
            values: categoryGenreOptions,
          },
          {
            key: 'country',
            label: 'Country',
            values: countryOptions,
          },
        ]);

        setSelections({
          category_genre: categoryGenreOptions?.[0]?.id || '',
          country: countryOptions?.[0]?.label || '',
        });
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setDashboardLoader(false);
    }
  };

  useEffect(() => {
    initializeChartData();
  }, [id]);

  useEffect(() => {
    if (!selections.category_genre || !categoryCountryMap) return;

    const selectedGenre = categoryCountryMap.values?.find(
      (genre: { name: string }) => genre.name === selections.category_genre,
    );

    const countryOptions = mapCountries(selectedGenre?.countries || []);

    const countryFilter: Filter = {
      key: 'country',
      label: 'Country',
      values: countryOptions,
    };

    setFilters((prevFilters) => {
      const otherFilters = prevFilters.filter((filter) => filter.key !== 'country');
      return [...otherFilters, countryFilter];
    });

    setSelections((prevSelections) => ({
      ...prevSelections,
      country: countryOptions?.[0]?.label || '',
    }));
  }, [selections.category_genre, categoryCountryMap]);

  const handleFilterChange = (key: string, selectedValue: string) => {
    let updatedSelections = { ...selections };

    if (key === 'category_genre') {
      updatedSelections = {
        ...updatedSelections,
        [key]: selectedValue,
        country: '',
      };
    } else {
      updatedSelections = { ...selections, [key]: selectedValue };
    }

    setSelections(updatedSelections);
  };

  return (
    <>
      {dashboardLoader && <FullPageLoader hasSideNav={true} />}
      <div
        id="pvx-ad-impressions-container"
        style={{
          width: '100%',
          height: '100vh',
          border: 'none',
          overflow: 'auto',
          padding: '1rem 2rem 5rem',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
            marginY: '1rem',
          }}
        >
          <Typography
            sx={{
              fontSize: '1.375rem',
              fontWeight: 700,
              color: '#162936',
              lineHeight: '1.875rem',
            }}
          >
            Ad Impressions Index
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '0.75rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 400,
                color: '#667085',
                lineHeight: '1.25rem',
              }}
            >
              Track how competitive your category is getting.
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            height: 'fit-content',
            borderRadius: '1.25rem',
            backgroundColor: '#FFFFFF',
            padding: '1rem',
            position: 'relative',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#6C6C6C',
              lineHeight: '1.25rem',
            }}
          >
            This index shows weekly changes in ad impressions across your category or genre,
            starting from a baseline of 100 for the week of Dec 30, 2024 to Jan 5, 2025. It helps
            understand whether rising CAC is due to increased market activity or internal campaign
            changes. While it doesn't directly reflect ad spends, it's a strong signal of shifting
            ad pressure by platform, channel, and region.
          </Typography>
        </Box>
        <AdImpressionsFilters
          filters={filters}
          handleFilterChange={handleFilterChange}
          selections={selections}
        />
        <AdImpressionsIndexSection
          chartList={dashboardData.filter(
            (c: ChartListData) =>
              c.chartType === AD_IMPRESSIONS_CHART_TYPE.AD_INDEX ||
              c.chartType === AD_IMPRESSIONS_CHART_TYPE.AD_SHARE,
          )}
          fetchDashboardData={fetchDashboardData}
          selections={selections}
        />
        {weekFilter.length > 0 ? (
          <WeeklyChangelog
            chartList={dashboardData.find(
              (c: ChartListData) =>
                c.chartType === AD_IMPRESSIONS_CHART_TYPE.AD_IMPRESSIONS_WEEKLY_CHANGELOG,
            )}
            fetchDashboardData={fetchDashboardData}
            selections={selections}
            handleFilterChange={handleFilterChange}
            weekFilter={weekFilter}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '440px',
              width: '100%',
              backgroundColor: '#FFFFFF',
              borderRadius: '1.25rem',
              minHeight: '440px',
            }}
          >
            <Loader />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            fontSize: '0.75rem',
            lineHeight: '1rem',
            color: '#162936',
            fontWeight: 500,
            alignItems: 'center',
            paddingRight: '1.25rem',
            marginTop: '1.5rem',
          }}
        >
          in partnership with
          <img src={appMagicLogo} alt="App Magic Logo" style={{ marginTop: '0.75rem' }} />
        </Box>
      </div>
    </>
  );
};

export default AdImpressions;
